import { PerformanceData, UnifiedClassEntry, PerformanceListItem } from '../types/performance';
import { formatPersonName } from './personName';

const formatDate = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  // dd/mm/yy
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const formatPrize = (entry: UnifiedClassEntry): number | null => {
  // Convert prize to EUR if not already
  if (typeof entry.unified_prize === 'number') {
    // If currency is EUR or null, use the prize directly
    if (!entry.unified_currency || entry.unified_currency === 'EUR') {
      return entry.unified_prize;
    }
    // For other currencies, still show the prize (conversion not implemented)
    return entry.unified_prize;
  }
  return null;
};

const asText = (val: unknown): string | null => {
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return val.toString();
  return null;
};

// Cleans up "- * -" (both parts missing) → null, and "- * X" (first part missing) → "X"
const cleanScore = (score: unknown): string | null => {
  const s = asText(score);
  if (!s) return null;
  const trimmed = s.trim();
  if (/^-\s*\*\s*-$/.test(trimmed)) return null;
  const match = trimmed.match(/^-\s*\*\s*(.+)$/);
  if (match) return match[1].trim() || null;
  return s;
};


const filterGoodEntries = (entry: UnifiedClassEntry): boolean => {
  const successCategory = entry.unified_success_category;
  const relativeRank = entry.unified_relative_rank;
  const videoUrl = entry.unified_clipmyhorse_url;
  
  const isGoodResult = successCategory === 1 && relativeRank !== null && relativeRank <= 0.25;
  const hasVideo = !!(videoUrl && videoUrl.trim().length > 0);
  
  return isGoodResult && hasVideo;
};

const filterBadEntries = (entry: UnifiedClassEntry): boolean => {
  const successCategory = entry.unified_success_category;
  const relativeRank = entry.unified_relative_rank;
  const videoUrl = entry.unified_clipmyhorse_url;
  
  const isBadResult = successCategory === 3 && relativeRank !== null && relativeRank >= 0.8;
  const hasVideo = !!(videoUrl && videoUrl.trim().length > 0);
  
  return isBadResult && hasVideo;
};

const toListItem = (entry: UnifiedClassEntry): PerformanceListItem => {
  // Build title from class name or competition name
  const title =
    asText(entry.unified_class_name) ||
    asText(entry.unified_competition_name) ||
    'Class entry';

  // Subtitle is the competition/event name
  const subtitle =
    asText(entry.unified_competition_name) ||
    null;

  return {
    title,
    subtitle,
    date: formatDate(entry.unified_class_date),
    dateRaw: entry.unified_class_date,
    rank: entry.unified_rank ?? null,
    numEntries: entry.unified_class_num_entries ?? null,
    obstacleHeight: entry.unified_height_cm ?? null,
    prizeEuro: formatPrize(entry),
    rider: formatPersonName(asText(entry.unified_rider_name)) || asText(entry.unified_rider_name),
    videoUrl: entry.unified_clipmyhorse_url,
    horsetelexUrl: entry.unified_horsetelex_url,
    equipeUrl: entry.unified_equipe_url,
    detail: asText(entry.unified_class_name),
    successCategory: entry.unified_success_category ?? null,
    score: cleanScore(entry.unified_result),
    eventCountry: entry.unified_location,
    eventCode: entry.unified_event_code,
    competitionName: entry.unified_competition_name,
    classId: entry.unified_class_id,
  };
};

const sortByDateDesc = (entries: UnifiedClassEntry[]): UnifiedClassEntry[] =>
  [...entries].sort((a, b) => {
    const aDate = a.unified_class_date ? new Date(a.unified_class_date).getTime() : 0;
    const bDate = b.unified_class_date ? new Date(b.unified_class_date).getTime() : 0;
    if (Number.isNaN(aDate) && Number.isNaN(bDate)) return 0;
    if (Number.isNaN(aDate)) return 1;
    if (Number.isNaN(bDate)) return -1;
    return bDate - aDate;
  });

export const buildHighlights = (data: PerformanceData): PerformanceListItem[] => {
  const goodEntries = data.classEntries.filter(filterGoodEntries);
  return sortByDateDesc(goodEntries).slice(0, 3).map(toListItem);
};

export const buildLowlights = (data: PerformanceData): PerformanceListItem[] => {
  const badEntries = data.classEntries.filter(filterBadEntries);
  return sortByDateDesc(badEntries).slice(0, 3).map(toListItem);
};

export const isClipMyHorseOnlyNoScore = (entry: UnifiedClassEntry): boolean => {
  const hasClipMyHorse = !!(entry.unified_clipmyhorse_url?.trim());
  const hasOtherSource =
    !!(entry.unified_horsetelex_url?.trim()) || !!(entry.unified_equipe_url?.trim());
  const hasScore =
    entry.unified_rank !== null ||
    !!(entry.unified_result?.trim()) ||
    entry.unified_first_round_faults !== null;
  return hasClipMyHorse && !hasOtherSource && !hasScore;
};

export const hasNoResults = (entry: UnifiedClassEntry): boolean => {
  const hasScore =
    entry.unified_rank !== null ||
    !!(entry.unified_result?.trim()) ||
    entry.unified_first_round_faults !== null;
  return !hasScore;
};

export type CompetitionHistoryScope = 'all' | 'results';

export const buildFullResults = (
  data: PerformanceData,
  scope: CompetitionHistoryScope = 'results'
): PerformanceListItem[] => {
  const filtered =
    scope === 'all'
      ? [...data.classEntries]
      : data.classEntries.filter((entry) => !hasNoResults(entry));
  return sortByDateDesc(filtered).map(toListItem);
};

export const hasPerformanceSeries = (data?: PerformanceData | null): boolean => {
  if (!data) return false;
  return (
    data.months.length > 0 &&
    (data.meanClearRoundMovingAvg.length > 0 ||
      data.maxObstacleHeight.length > 0 ||
      data.sumPrizeInEuro.length > 0)
  );
};
